import { PageHeader } from '@/components/page-layout';
import { Quest } from '@/types';
import { Box, Card, Heading, HStack, Image, Skeleton, Text, VStack } from '@chakra-ui/react';
import { CompletionInfo, UsersCompleted } from '../../components/quest-card';
import { DoubleRewardsInfo, XPBadge } from '../Quests/LatestQuestCard';
type Props = {
  quest: Quest;
};

export const QuestDetailsMainCardSkeleton = () => {
  const imageSkeleton = (
    <Skeleton
      rounded="16px"
      maxW={{
        base: '100%',
        lg: '400px',
        xl: '520px',
      }}
      w="100%"
      aspectRatio={1}
    />
  );

  return (
    <>
      <VStack hideFrom="lg" gap={6} alignItems="flex-start" w="full">
        <VStack gap={2} alignItems="flex-start">
          <Skeleton height="27px" width="80px" />
          <Skeleton height="72px" width="300px" />
        </VStack>
        <Skeleton height="80px" width="100%" />
        {imageSkeleton}
      </VStack>
      <Card.Root
        overflow="hidden"
        hideBelow="lg"
        w="full"
        p={0}
        display="flex"
        flexDirection={{
          base: 'column',
          lg: 'row',
        }}
        rounded="16px"
        justifyContent="space-between"
      >
        <Card.Body
          px={10}
          py={12}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-start"
          w="full"
        >
          <Skeleton height="40px" width="100%" />
          <VStack gap={3} alignItems="flex-start" mb={6}>
            <Skeleton height="40px" width="100%" />
            <Skeleton height="40px" width="100%" />
            <Skeleton height="40px" width="100%" />
          </VStack>
          <Skeleton height="40px" width="100%" />
          <Skeleton height="40px" width="100%" />
        </Card.Body>
        {imageSkeleton}
      </Card.Root>
    </>
  );
};

export const QuestDetailsMainCard = ({ quest }: Props) => {
  const isAllCompleted = quest.tasks.every((task) => task.completed);
  const percentCompleted = (
    (quest.tasks.reduce((acc, task) => acc + (task.completed ? 1 : 0), 0) / quest.tasks.length) *
    100
  ).toFixed(0);

  const completionInfo = (
    <HStack
      position="absolute"
      left={6}
      right={6}
      bottom={6}
      justifyContent="space-between"
      alignItems="center"
      py={4}
      px={6}
      color="white"
      rounded="16px"
      bg="rgba(255, 255, 255, 0.20)"
      backdropFilter="blur(10px)"
    >
      <Heading fontSize="xl" fontWeight="500" lineHeight="32px" color="#111">
        {isAllCompleted ? 'Completed' : `${percentCompleted}%`}
      </Heading>
      <CompletionInfo
        percentCompleted={Number(percentCompleted)}
        isAllCompleted={isAllCompleted}
        size="large"
        hideText
      />
    </HStack>
  );

  const image = (
    <Box position="relative">
      <Image
        src={`/assets/quests/wave-${quest.wave}.avif`}
        alt="Quest"
        aspectRatio={1}
        rounded="16px"
        maxW={{
          base: '100%',
          lg: '400px',
          xl: '520px',
        }}
        w="100%"
      />
      {completionInfo}
    </Box>
  );

  const waveLabel = (
    <HStack gap={4} alignItems="center" mb={[0, 0, 0, 4]}>
      <Text fontSize="lg" fontWeight="500" color="text.secondary">
        Quest {quest.wave}
      </Text>
    </HStack>
  );

  const doubleRewardsLabel = quest.isDoubleRewards ? (
    <DoubleRewardsInfo
      endDate={quest.doubleRewardsEndTime}
      bg="none"
      color={['text.secondary', 'text.secondary', 'text.secondary', 'white']}
      position="relative"
      p={0}
      mb={[0, 0, 0, 6]}
    />
  ) : null;

  const questStats = (
    <HStack gap={4} mt="auto">
      <XPBadge xpReward={Number(quest.xpReward)} />
      <UsersCompleted numUsersCompleted={quest.numUsersCompleted ?? 0} />
    </HStack>
  );

  return (
    <>
      <VStack hideFrom="lg" gap={6} alignItems="flex-start">
        <VStack gap={2} alignItems="flex-start">
          {waveLabel}
          <PageHeader title={quest.title} tertiaryTitle={quest.description} />
        </VStack>
        {doubleRewardsLabel}
        {questStats}
        {image}
      </VStack>
      <Card.Root
        overflow="hidden"
        hideBelow="lg"
        w="full"
        p={0}
        display="flex"
        flexDirection={{
          base: 'column',
          lg: 'row',
        }}
        rounded="16px"
        justifyContent="space-between"
      >
        <Card.Body
          px={10}
          py={12}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          {waveLabel}
          <VStack gap={3} alignItems="flex-start" mb={6}>
            <Heading fontSize="2xl" fontWeight="bold" lineHeight="32px">
              {quest.title}
            </Heading>
            <Text
              fontWeight="500"
              color="text.secondary"
              lineHeight="150%"
              lineClamp={['none', 'none', 'none', 2, 'none']}
            >
              {quest.description}
            </Text>
          </VStack>
          {doubleRewardsLabel}
          {questStats}
        </Card.Body>
        {image}
      </Card.Root>
    </>
  );
};
